import { Component, OnDestroy, OnInit } from '@angular/core';
import { IWeissmanModalComponent } from '../../../../../Compliance/WeissmanModalService';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../../../../Compliance/AgGrid';
import { AgGridMultiSelectedCellRenderer, AgGridMultiSelectedHeaderRenderer, AgGridMultiSelectRendererParams, AgGridMultiSelectTracker } from '../../../../../Compliance/AgGrid/MultiSelectTracker';
import { Subscription } from 'rxjs';
import { StateSummary } from '../../../../../Common/States/state.model';
import { ValuationTemplateService } from '../../../valuationTemplate.service';
import { SiteClassPrimary } from '../../../../../Core-Repositories/siteClass.repository';

export interface AddSiteClassModalParams {
    valuationTemplateId: number;
    siteClassesSelected: number[];
}

@Component({
    selector: 'add-site-class-modal',
    templateUrl: './addSiteClass.component.html',
    standalone: false
})
export class AddSiteClassComponent implements OnDestroy, IWeissmanModalComponent<AddSiteClassModalParams, Core.ValuationTemplateModel>, OnInit {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _valuationTemplateService: ValuationTemplateService
    ) {
    }

    params: AddSiteClassModalParams;
    result: Core.ValuationTemplateModel;

    loading: boolean = true;
    hasChanges: boolean;
    gridTracker: AgGridMultiSelectTracker;
    gridOptions: GridOptions = new AgGridOptionsBuilder({
        rowClassRules: {
            'ag-row-selected': (params) => params.data && this.gridTracker.isRowSelected((params.data as Weissman.Model.SiteClasses.SiteClassPrimary).siteClassPrimaryID)
        }
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withFloatingFilter()
        .withMultipleColumnSort()
        .withTextSelection()
        .build();
    gridApi: GridApi;

    private _allowedSiteClasses: SiteClassPrimary[];
    private _selectableSiteClasses: SiteClassPrimary[];
    private _selectedRowsSub: Subscription;

    async ngOnInit(): Promise<void> {
        this._allowedSiteClasses = await this._valuationTemplateService.getSiteClassOptions();
        this._selectableSiteClasses = this._allowedSiteClasses.filter(x => !this.params.siteClassesSelected.includes(x.siteClassPrimaryID));
        this._setRowData();
    }

    ngOnDestroy(): void {
        this._selectedRowsSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        this.gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));

        this._selectedRowsSub = this.gridTracker.selectedRows$.subscribe(x => {
            this.hasChanges = (x.selectAllRows && x.selectedRows.length !== this._selectableSiteClasses.length)
                || (!x.selectAllRows && !!x.selectedRows.length);
        });

        const columns: ColDef[] = [
            {
                field: 'siteClassPrimaryID',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: {
                    tracker: this.gridTracker
                } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.gridTracker
                } as AgGridMultiSelectRendererParams
            },
            {
                headerName: 'Description',
                field: 'siteClassPrimaryDesc',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth
            }
        ];

        this.gridApi.setGridOption('columnDefs', columns);
        this._setRowData();
    }

    async save(): Promise<void> {
        this.loading = true;
        try {
            const rowModel = this.gridTracker.getSelectedRowsModel();
            const siteClasses = this._allowedSiteClasses
                .reduce((acc, x) => {
                    const row = rowModel.selectedRows.find(y => x.siteClassPrimaryID === y);
                    if ((!rowModel.selectAllRows && row) || (rowModel.selectAllRows && !row) || this.params.siteClassesSelected.includes(x.siteClassPrimaryID)) {
                        acc.push({
                            siteClassPrimaryId: x.siteClassPrimaryID,
                            valuationTemplateId: this.params.valuationTemplateId
                        });
                    }
                    return acc;
                }, [] as Core.ValuationTemplateSiteClassPrimaryModel[]);

            this.result = await this._valuationTemplateService.updateTemplateSiteClasses(this.params.valuationTemplateId, siteClasses);
        } finally {
            this.cancel();
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    private _setRowData(): void {
        if (!this.gridApi || !this._selectableSiteClasses) {
            return;
        }

        this.gridApi.setGridOption('rowData', this._selectableSiteClasses);
        this.gridApi.sizeColumnsToFit();
        this.loading = false;
    }

    private _getGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort(x => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        return Promise.resolve({
            data: rows.map(x => {
                const state = x.data as StateSummary;
                return state && state.stateID;
            })
        } as Compliance.QueryResultModel<number>);
    }

}
